/**
 * CommentItem - Individual comment display with actions
 */
import React, { useState } from "react"
import { Button } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faReply,
  faCheck,
  faTrash,
  faChevronDown,
  faChevronUp,
  faArrowUpRightFromSquare,
  faPen,
} from "@fortawesome/free-solid-svg-icons"
import { Comment, CommentStatus } from "types/comments.types"
import { formatRelativeTime } from "utils/relativeTime"
import CommentReplyItem from "./CommentReply.component"

interface CommentItemProps {
  comment: Comment
  isActive: boolean
  onSelect: (commentId: string) => void
  onNavigate: (commentId: string) => void
  onReply: (commentId: string) => void
  onStatusChange: (commentId: string, status: CommentStatus) => void
  onDelete: (commentId: string) => void
  onResolve: (commentId: string) => void
  onUpdate?: (commentId: string, content: string) => void
  onDeleteReply?: (commentId: string, replyId: string) => void
}

/**
 * Parse backend date format (yyyy-mm-dd hh:mm:ss) to Date object
 * Also handles ISO 8601 format for backwards compatibility
 * Returns current date if dateString is undefined (e.g., during Yjs sync before backend response)
 */
const parseDate = (dateString: string | undefined): Date => {
  if (!dateString) return new Date()
  // Backend format: "2025-12-01 14:30:45" - replace space with T for ISO 8601
  const normalizedDate = dateString.includes("T")
    ? dateString
    : dateString.replace(" ", "T")
  return new Date(normalizedDate)
}

/**
 * Get status badge class
 */
const getStatusBadgeClass = (status: CommentStatus): string => {
  switch (status) {
    case "open":
      return "comment-status-badge open"
    case "in_progress":
      return "comment-status-badge in_progress"
    case "resolved":
      return "comment-status-badge resolved"
    default:
      return "comment-status-badge open"
  }
}

/**
 * Get status display text
 */
const getStatusText = (status: CommentStatus): string => {
  switch (status) {
    case "open":
      return "Open"
    case "in_progress":
      return "In Progress"
    case "resolved":
      return "Resolved"
    default:
      return "Open"
  }
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  isActive,
  onSelect,
  onNavigate,
  onReply,
  onStatusChange,
  onDelete,
  onResolve,
  onUpdate,
  onDeleteReply,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(comment.content)

  const hasReplies = comment.replies.length > 0

  const handleClick = () => {
    onSelect(comment.id)
  }

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation()
    onNavigate(comment.id)
  }

  const handleReply = (e: React.MouseEvent) => {
    e.stopPropagation()
    onReply(comment.id)
  }

  const handleResolve = (e: React.MouseEvent) => {
    e.stopPropagation()
    onResolve(comment.id)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(comment.id)
  }

  const handleStatusChange = (status: CommentStatus) => {
    onStatusChange(comment.id, status)
    setShowStatusMenu(false)
  }

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditContent(comment.content)
    setIsEditing(true)
  }

  const handleSaveEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (editContent.trim() && editContent !== comment.content && onUpdate) {
      onUpdate(comment.id, editContent.trim())
    }
    setIsEditing(false)
  }

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditContent(comment.content)
    setIsEditing(false)
  }

  return (
    <div
      className={`border rounded-lg transition-all cursor-pointer ${
        isActive
          ? "border-cyan-400 bg-cyan-50 shadow-md"
          : "border-gray-200 hover:border-cyan-300 hover:bg-cyan-50/50"
      }`}
      onClick={handleClick}
    >
      {/* Main comment */}
      <div className="p-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Author avatar placeholder */}
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
              style={{ backgroundColor: "var(--dq-brand)" }}
            >
              {comment.authorName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm text-gray-900 truncate">
                {comment.authorName}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{formatRelativeTime(parseDate(comment.createdAt))}</span>
                <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium">
                  {comment.variantName || "master"}
                </span>
              </div>
            </div>
          </div>

          {/* Status badge */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowStatusMenu(!showStatusMenu)
              }}
              className={getStatusBadgeClass(comment.status)}
            >
              {getStatusText(comment.status)}
            </button>

            {/* Status dropdown */}
            {showStatusMenu && (
              <div
                className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100"
                  onClick={() => handleStatusChange("open")}
                >
                  Open
                </button>
                <button
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100"
                  onClick={() => handleStatusChange("in_progress")}
                >
                  In Progress
                </button>
                <button
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100"
                  onClick={() => handleStatusChange("resolved")}
                >
                  Resolved
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Original text preview */}
        {comment.originalText && (
          <div className="mb-2 px-2 py-1.5 bg-yellow-50 border-l-2 border-yellow-400 rounded-r text-xs text-gray-600 italic line-clamp-1">
            "{comment.originalText}"
          </div>
        )}

        {/* Comment content */}
        {isEditing ? (
          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full border border-gray-300 rounded p-2 text-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none resize-none"
              rows={3}
              autoFocus
              maxLength={2000}
            />
            <div className="flex gap-2 justify-end">
              <Button onClick={handleCancelEdit}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSaveEdit}
                disabled={
                  !editContent.trim() || editContent === comment.content
                }
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
            {comment.content}
          </div>
        )}

        {/* Actions */}
        {!isEditing && (
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-1">
              <Button
                type="text"
                icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                onClick={handleNavigate}
                title="Go to comment in editor"
                aria-label="Go to comment in editor"
              />
              <Button
                type="text"
                icon={<FontAwesomeIcon icon={faReply} />}
                onClick={handleReply}
                title="Reply"
                aria-label="Reply"
              />
              {onUpdate && (
                <Button
                  type="text"
                  icon={<FontAwesomeIcon icon={faPen} />}
                  onClick={handleEdit}
                  title="Edit"
                  aria-label="Edit"
                />
              )}
              {comment.status !== "resolved" && (
                <Button
                  type="text"
                  icon={<FontAwesomeIcon icon={faCheck} />}
                  onClick={handleResolve}
                  title="Resolve"
                  aria-label="Resolve"
                />
              )}
              <Button
                type="text"
                danger
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={handleDelete}
                title="Delete"
                aria-label="Delete"
              />
            </div>

            {/* Replies toggle */}
            {hasReplies && (
              <button
                onClick={toggleExpanded}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-500"
              >
                <span>
                  {comment.replies.length} repl
                  {comment.replies.length === 1 ? "y" : "ies"}
                </span>
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronUp : faChevronDown}
                  size="xs"
                />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Replies section */}
      {hasReplies && isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <div className="p-2 space-y-2">
            {comment.replies.map((reply) => (
              <CommentReplyItem
                key={reply.id}
                reply={reply}
                onDelete={
                  onDeleteReply
                    ? () => onDeleteReply(comment.id, reply.id)
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CommentItem
